import React, { useState } from "react";
import { toast, Toaster } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { MessageCircle, Send, Mail, HelpCircle, ChevronDown, LifeBuoy } from "lucide-react";
import { api, formatError } from "../lib/api";

const DISCORD_URL = "https://discord.gg/nEvwBUtqAV";

const FAQS = [
  {
    q: "How do I register my team?",
    a: "Join our Discord and open a ticket in #tournament-signups with your team name and roster. An admin will add you to the bracket.",
  },
  {
    q: "When do tournaments run?",
    a: "Most events run on weekends. Upcoming dates are posted on the Tournaments page and announced in Discord.",
  },
  {
    q: "How are prize pools distributed?",
    a: "Prizes are paid out within 72 hours after Grand Finals, directly to the winning team captain.",
  },
  {
    q: "I found a bug on the site — where do I report it?",
    a: "Ping any admin in Discord or send us a message using the form on this page.",
  },
];

export default function SupportPage() {
  const [form, setForm] = useState({ name: "", email: "", topic: "general", message: "" });
  const [sending, setSending] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we'll reply in Discord");
      setForm({ name: "", email: "", topic: "general", message: "" });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
    setSending(false);
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Toaster position="top-right" theme="dark" toastOptions={{ className: "glass-dark !border-red-500/30" }} />
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.3em] text-red-400 font-mono">We're here to help</div>
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mt-2">Support &amp; Contact</h1>
          <p className="text-zinc-400 mt-3 max-w-2xl">
            The fastest response is in Discord — but you can also email us directly or use the form below.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-12">
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="support-discord-card"
            className="glass-red-glow rounded-2xl p-6 hover:shadow-[0_0_30px_rgba(230,0,0,0.3)] transition-all"
          >
            <div className="h-12 w-12 rounded-full bg-gradient-to-b from-red-500/80 to-red-950/80 border border-red-400/40 flex items-center justify-center mb-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.3),0_0_16px_rgba(230,0,0,0.5)]">
              <MessageCircle size={18} className="text-white" />
            </div>
            <h3 className="font-display font-bold text-xl mb-1">Discord Support</h3>
            <p className="text-sm text-zinc-400">Live chat with mods, admins and the community 24/7.</p>
          </a>
          <a
            href="mailto:spicy.community.gg@gmail.com"
            data-testid="support-email-card"
            className="glass-dark rounded-2xl p-6 hover:border-red-500/40 transition-all"
          >
            <div className="h-12 w-12 rounded-full glass-red flex items-center justify-center mb-4">
              <Mail size={18} className="text-red-300" />
            </div>
            <h3 className="font-display font-bold text-xl mb-1">Email</h3>
            <p className="text-sm text-zinc-400">spicy.community.gg@gmail.com — typical reply within 24h.</p>
          </a>
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="support-admin-card"
            className="glass-dark rounded-2xl p-6 hover:border-red-500/40 transition-all"
          >
            <div className="h-12 w-12 rounded-full glass-red flex items-center justify-center mb-4">
              <LifeBuoy size={18} className="text-red-300" />
            </div>
            <h3 className="font-display font-bold text-xl mb-1">Admin Help</h3>
            <p className="text-sm text-zinc-400">Bracket disputes, score corrections and urgent issues.</p>
          </a>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <div className="glass-dark rounded-2xl p-6 md:p-8">
              <h2 className="font-display font-bold text-2xl mb-1">Send us a message</h2>
              <p className="text-sm text-zinc-400 mb-6">Fill this out and we'll get back to you in Discord or by email.</p>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <label className="block">
                    <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Name</span>
                    <input
                      data-testid="contact-name" required value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-500"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Email</span>
                    <input
                      data-testid="contact-email" type="email" required value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-500"
                    />
                  </label>
                </div>
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Topic</span>
                  <select
                    data-testid="contact-topic" value={form.topic}
                    onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 focus:outline-none focus:border-red-500"
                  >
                    <option value="general">General question</option>
                    <option value="tournament">Tournament / Bracket</option>
                    <option value="account">Account / Team</option>
                    <option value="bug">Report a bug</option>
                    <option value="partner">Partnership / Sponsor</option>
                  </select>
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">Message</span>
                  <textarea
                    data-testid="contact-message" required rows={5} value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-1 w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 focus:outline-none focus:border-red-500 resize-none"
                  />
                </label>
                <button
                  type="submit" disabled={sending}
                  data-testid="contact-submit"
                  className="btn-liquid btn-liquid-lg"
                >
                  <Send size={16} /> {sending ? "Sending..." : "Send message"}
                </button>
              </form>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-5">
              <HelpCircle size={18} className="text-red-400" />
              <h3 className="font-display font-bold text-xl">FAQ</h3>
            </div>
            <div className="space-y-3">
              {FAQS.map((f, i) => (
                <button
                  key={i}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  data-testid={`faq-item-${i}`}
                  className="w-full text-left glass-dark rounded-xl p-4 hover:border-red-500/30 transition-all"
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-semibold text-sm">{f.q}</span>
                    <ChevronDown
                      size={16}
                      className={`text-red-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`}
                    />
                  </div>
                  {openFaq === i && (
                    <div className="mt-3 text-zinc-400 text-sm leading-relaxed">{f.a}</div>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer discordUrl={DISCORD_URL} />
    </div>
  );
}
